import { useState } from "react";
import useFormSubmit from "@/hooks/useFormSubmit";
import { tipCategories } from "@/mocks/siteData";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah8k83oh653ivfvnoa0";

const materialOptions = ["文字描述", "截图照片", "视频素材", "录音文件"];

export default function TipForm() {
  const [refCode] = useState(
    () => `QS-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
  );
  const [materials, setMaterials] = useState<string[]>(["文字描述"]);
  const [detail, setDetail] = useState("");
  const [anonymous, setAnonymous] = useState("完全匿名");

  const { status, message, submit } = useFormSubmit(
    SUBMIT_ADDR,
    `线索已加密送达，编辑会尽快处理。你的线索编号为 ${refCode}，请自行记录以便后续查询。`,
  );

  const toggleMaterial = (value: string) => {
    setMaterials((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
  };

  return (
    <section id="tips" className="scroll-mt-28 w-full px-4 md:px-8 py-16 md:py-24 bg-background-100">
      <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-4">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-primary-600">
            <span className="w-6 h-px bg-primary-400"></span>
            提交线索
          </span>
          <h2 className="mt-4 font-heading text-2xl md:text-[2rem] font-bold text-foreground-950 leading-snug">
            填写线索信息
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-foreground-600">
            表单只收集核实线索所必需的信息。手机号、邮箱均为选填，
            <strong className="font-medium text-foreground-900">不填写同样可以完成提交</strong>。
          </p>

          <ul className="mt-7 space-y-4">
            <li className="flex items-start gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50 text-primary-600 border border-background-200 shrink-0">
                <i className="ri-lock-2-line"></i>
              </span>
              <span className="text-sm leading-relaxed text-foreground-600">
                页面不保存草稿，关闭即清空，降低信息残留风险。
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50 text-primary-600 border border-background-200 shrink-0">
                <i className="ri-file-shield-2-line"></i>
              </span>
              <span className="text-sm leading-relaxed text-foreground-600">
                编辑收到后会先做身份脱敏，再进入事实核实环节。
              </span>
            </li>
            {materials.includes("视频素材") || materials.includes("录音文件") ? (
              <li className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 shrink-0">
                  <i className="ri-link-m"></i>
                </span>
                <span className="text-sm leading-relaxed text-foreground-600">
                  你选择了视频或录音素材：提交后编辑会通过一次性安全链接引导你上传原文件。
                </span>
              </li>
            ) : null}
          </ul>
        </div>

        <div className="lg:col-span-8">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <form id="tip-submit-form" data-readdy-form onSubmit={submit} noValidate={false}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="tip_title"
                    className="block text-sm font-medium text-foreground-900 mb-2"
                  >
                    线索标题 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="tip_title"
                    name="tip_title"
                    type="text"
                    required
                    maxLength={60}
                    placeholder="用一句话概括你要反映的事情"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="tip_type"
                    className="block text-sm font-medium text-foreground-900 mb-2"
                  >
                    线索类型 <span className="text-accent-600">*</span>
                  </label>
                  <select
                    id="tip_type"
                    name="tip_type"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors cursor-pointer"
                  >
                    <option value="" disabled>
                      请选择线索所属类型
                    </option>
                    {tipCategories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="tip_region"
                    className="block text-sm font-medium text-foreground-900 mb-2"
                  >
                    涉及地区 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="tip_region"
                    name="tip_region"
                    type="text"
                    required
                    maxLength={40}
                    placeholder="例如：江苏 南京 / 线上平台"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                  />
                </div>

                <div className="sm:col-span-2">
                  <span className="block text-sm font-medium text-foreground-900 mb-2">
                    你手里有哪些素材
                  </span>
                  <div className="flex flex-wrap gap-2.5">
                    {materialOptions.map((option) => {
                      const active = materials.includes(option);
                      return (
                        <label
                          key={option}
                          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md border text-sm cursor-pointer transition-colors ${
                            active
                              ? "border-primary-400 bg-primary-100 text-primary-700"
                              : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            name="material_type"
                            value={option}
                            checked={active}
                            onChange={() => toggleMaterial(option)}
                            className="w-4 h-4 accent-primary-500"
                          />
                          {option}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="tip_detail" className="block text-sm font-medium text-foreground-900">
                      线索描述 <span className="text-accent-600">*</span>
                    </label>
                    <span
                      className={`text-xs ${detail.length > 500 ? "text-accent-700" : "text-foreground-400"}`}
                    >
                      {detail.length} / 500
                    </span>
                  </div>
                  <textarea
                    id="tip_detail"
                    name="tip_detail"
                    required
                    rows={5}
                    maxLength={500}
                    value={detail}
                    onChange={(event) => setDetail(event.target.value.slice(0, 500))}
                    placeholder="说清时间、地点、涉及方和事情经过即可，不必写得很长。细节可以后续在编辑回访时补充。"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors resize-none"
                  />
                </div>

                <div className="sm:col-span-2">
                  <span className="block text-sm font-medium text-foreground-900 mb-2">
                    投稿方式
                  </span>
                  <div className="flex flex-col sm:flex-row gap-2.5">
                    {["完全匿名", "可留联系方式"].map((option) => (
                      <label
                        key={option}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-md border text-sm cursor-pointer transition-colors ${
                          anonymous === option
                            ? "border-primary-400 bg-primary-100 text-primary-700"
                            : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="anonymous"
                          value={option}
                          checked={anonymous === option}
                          onChange={() => setAnonymous(option)}
                          className="w-4 h-4 accent-primary-500"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>

                {anonymous === "可留联系方式" && (
                  <div className="sm:col-span-2">
                    <label
                      htmlFor="contact_method"
                      className="block text-sm font-medium text-foreground-900 mb-2"
                    >
                      联系方式（选填）
                    </label>
                    <input
                      id="contact_method"
                      name="contact_method"
                      type="text"
                      maxLength={60}
                      autoComplete="off"
                      placeholder="建议使用临时邮箱或未绑定真实姓名的账号"
                      className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
                    />
                    <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                      不使用与真实身份强绑定的工作邮箱或实名手机号，能显著降低暴露风险。
                    </p>
                  </div>
                )}

                <input type="hidden" name="reference_code" value={refCode} readOnly />

                <div className="form-guard-field" aria-hidden="true">
                  <label htmlFor="website_alt">请勿填写此栏</label>
                  <input
                    id="website_alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>
              </div>

              {status === "success" && (
                <p className="mt-6 flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-100 px-4 py-3 text-sm text-primary-700">
                  <i className="ri-shield-check-line mt-0.5"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="mt-6 flex items-start gap-2.5 rounded-md border border-accent-300 bg-accent-100 px-4 py-3 text-sm text-accent-800">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  {message}
                </p>
              )}

              <div className="mt-7 flex flex-col sm:flex-row items-center gap-4">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"}></i>
                  {status === "submitting" ? "正在加密提交…" : "提交线索"}
                </button>
                <p className="text-xs leading-relaxed text-foreground-500">
                  提交即表示你已阅读
                  <span className="text-primary-600">《投稿与隐私说明》</span>
                  ，同意我们在核实范围内使用该线索。
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}